import { useTranslation } from "react-i18next";
import { LayoutGrid, List } from "lucide-react";
import "./ViewModeToggle.css";

export type ViewMode = "thumbnails" | "list";

export interface ViewModeToggleProps {
  /** Current view mode. Accepts extended types (e.g., "folders") for display, but toggle only emits "thumbnails" | "list" */
  mode: ViewMode | (string & {});
  /** Called when toggle is clicked. Always receives "thumbnails" or "list" */
  onChange: (mode: ViewMode) => void;
}

/**
 * ViewModeToggle - Toggle buttons for switching between thumbnails/list view modes
 * Extracted to shared for use in AssetDrawer and AssetsPanel
 *
 * The mode prop accepts any string to support consumers with extended view modes
 * (e.g., "folders"), but the onChange callback always emits ViewMode values.
 */
export function ViewModeToggle({
  mode,
  onChange,
}: ViewModeToggleProps): React.ReactElement {
  const { t } = useTranslation();

  const thumbnailsLabel = t("common.thumbnailsView", "Thumbnails view");
  const listLabel = t("common.listView", "List view");
  const viewModeLabel = t("common.viewMode", "View mode");

  return (
    <div className="flex gap-1" role="group" aria-label={viewModeLabel}>
      <button
        type="button"
        className={`view-mode-btn ${mode === "thumbnails" ? "active" : ""}`}
        onClick={() => onChange("thumbnails")}
        title={thumbnailsLabel}
        aria-label={thumbnailsLabel}
        aria-pressed={mode === "thumbnails"}
      >
        <LayoutGrid size={18} />
      </button>
      <button
        type="button"
        className={`view-mode-btn ${mode === "list" ? "active" : ""}`}
        onClick={() => onChange("list")}
        title={listLabel}
        aria-label={listLabel}
        aria-pressed={mode === "list"}
      >
        <List size={18} />
      </button>
    </div>
  );
}
